import React, { useState, useMemo } from 'react';
import { Input, Button, Table, Badge, Tooltip } from 'antd';
import { SearchOutlined, FolderOpenOutlined, CloseCircleOutlined } from '@ant-design/icons';

export default function DataTable({ data, onItemSelect }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('oid');
    const [sortDirection, setSortDirection] = useState('asc');

    const filteredAndSortedData = useMemo(() => {
        const filtered = data.filter(
            (item) =>
                item.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.oid.toString().includes(searchTerm)
        );

        filtered.sort((a, b) => {
            const aValue = a[sortField];
            const bValue = b[sortField];

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }

            return 0;
        });

        return filtered;
    }, [data, searchTerm, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    const columns = [
        {
            title: (
                <span onClick={() => handleSort('oid')} style={{ cursor: 'pointer' }}>
                    # {sortField === 'oid' && (sortDirection === 'asc' ? '↑' : '↓')}
                </span>
            ),
            dataIndex: 'oid',
            width: 50,
            align: 'center',
            key: 'oid',
            sorter: false,
            render: (oid) => <Badge count={oid} style={{ backgroundColor: '#52c41a' }} />,
        },
        {
            title: (
                <span onClick={() => handleSort('nombre')} style={{ cursor: 'pointer' }}>
                    Nombre {sortField === 'nombre' && (sortDirection === 'asc' ? '↑' : '↓')}
                </span>
            ),
            width: 350,
            dataIndex: 'nombre',
            key: 'nombre',
            render: (text) => (
                <Tooltip title={text}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</div>
                </Tooltip>
            ),
        },
        {
            title: 'Descripción',
            dataIndex: 'descripcion',
            key: 'descripcion',
            render: (text) => (
                <div style={{ maxWidth: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {text}
                </div>
            ),
        },
        {
            title: 'Acciones',
            key: 'acciones',
            align: 'center',
            width: 100,
            render: (_, record) => {
                if (!record.rutas || record.rutas.length === 0) {
                    // 🛑 Sin rutas = sin carpetas
                    return <span style={{ color: '#999' }}>Sin rutas</span>;
                }

                return (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>

                        {record.rutas.map((ruta, index) => (
                            <Tooltip key={ruta.oid} title={`Abrir carpeta: ${ruta.ruta}`}>
                                <Button
                                    type="text"
                                    icon={<FolderOpenOutlined style={{ fontSize: 22 }} />}
                                    onClick={() => onItemSelect(record, ruta)}
                                />
                            </Tooltip>
                        ))}
                    </div>
                );
            },
        }
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                <Input
                    placeholder="Buscar por nombre o descripción"
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ maxWidth: 400 }}
                    suffix={
                        searchTerm && (
                            <CloseCircleOutlined
                                onClick={clearSearch}
                                style={{ cursor: 'pointer', color: '#999' }}
                            />
                        )
                    }
                />
                <Badge count={`${filteredAndSortedData.length} / ${data.length} elementos`} />
            </div>
            <Table
                dataSource={filteredAndSortedData}
                columns={columns}
                rowKey="oid"
                pagination={{ pageSize: 5 }}
                bordered
            />
        </div>
    );
}
